import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Sparkles, Loader2 } from "lucide-react";

const TIPOS = [
  { v: "hotel", l: "Hotel" },
  { v: "transfer", l: "Transfer" },
  { v: "actividad", l: "Actividad" },
];
const LANGS = [
  { v: "es", l: "Español" },
  { v: "en", l: "English" },
  { v: "it", l: "Italiano" },
];

const VARS = [
  "sender_name", "sender_department",
  "client_name", "client_email", "client_phone",
  "pax", "adults", "children",
  "date", "time",
  "service", "activity",
  "origin", "destination", "meeting_point",
  "flight_number", "arrival_date", "departure_date",
  "checkin", "checkout",
  "provider_name", "notes",
];

function emptyTpl() {
  return { name: "", type: "actividad", language: "es", subject: "", body: "", is_default: false };
}

export default function TemplatesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTpl());
  const [translating, setTranslating] = useState(false);

  const fetchAll = async () => {
    const { data } = await api.get("/templates");
    setItems(data);
  };

  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyTpl()); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...t }); setOpen(true); };

  const onSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/templates/${editing.id}`, form);
      else await api.post(`/templates`, form);
      toast.success("Plantilla guardada");
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const onDelete = async (t) => {
    if (!confirm(`¿Borrar plantilla "${t.name}"?`)) return;
    await api.delete(`/templates/${t.id}`);
    fetchAll();
  };

  const onTranslateForm = async () => {
    const targets = LANGS.filter((l) => l.v !== form.language).map((l) => l.v);
    const target = window.prompt(`Traducir a (${targets.join(" / ")}):`, targets[0]);
    if (!target || !targets.includes(target)) return;
    setTranslating(true);
    try {
      const { data } = await api.post("/translate", {
        subject: form.subject, body: form.body,
        source_lang: form.language, target_lang: target,
      });
      setForm({ ...form, subject: data.subject, body: data.body, language: target });
      toast.success("Plantilla traducida");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al traducir");
    } finally {
      setTranslating(false);
    }
  };

  const insertVar = (v) => {
    setForm((f) => ({ ...f, body: (f.body || "") + ` {{${v}}}` }));
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="vv-label">Configuración</p>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Plantillas de correo</h1>
          <p className="text-sm text-gray-500 mt-1">Hotel, Transfer y Actividad en 3 idiomas. Usa variables como <code className="mono">{"{{client_name}}"}</code>.</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={openNew} data-testid="create-template-button">
          <Plus size={16} className="mr-1"/> Nueva plantilla
        </Button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((t) => (
          <div key={t.id} className="vv-card p-5" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-500">{t.type} · {t.language}</p>
                <h3 className="text-base font-semibold mt-1">{t.name}</h3>
                <p className="text-sm text-gray-600 mt-1 line-clamp-1">{t.subject}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(t)} className="text-gray-400 hover:text-blue-700" title="Editar"><Pencil size={14}/></button>
                <button onClick={() => onDelete(t)} className="text-gray-400 hover:text-red-600" title="Borrar"><Trash2 size={14}/></button>
              </div>
            </div>
            <pre className="mt-3 text-[12px] font-sans text-gray-700 whitespace-pre-wrap line-clamp-6 bg-gray-50 border border-[var(--vv-border)] p-3 rounded-md">{t.body}</pre>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle></DialogHeader>
          <form onSubmit={onSave} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-3">
                <Label className="vv-label">Nombre *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name-input"/>
              </div>
              <div>
                <Label className="vv-label">Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="template-type-select"><SelectValue/></SelectTrigger>
                  <SelectContent>{TIPOS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="vv-label">Idioma</Label>
                <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                  <SelectTrigger data-testid="template-language-select"><SelectValue/></SelectTrigger>
                  <SelectContent>{LANGS.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button type="button" variant="outline" size="sm" className="h-9 text-xs" onClick={onTranslateForm} disabled={translating || !form.body} data-testid="template-translate-button">
                  {translating ? <Loader2 size={12} className="mr-1 animate-spin"/> : <Sparkles size={12} className="mr-1"/>}
                  Traducir con IA
                </Button>
              </div>
            </div>

            <div>
              <Label className="vv-label">Asunto *</Label>
              <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="template-subject-input"/>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="vv-label">Cuerpo *</Label>
                <span className="text-[11px] text-gray-500">Variables disponibles → haz clic para insertar</span>
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {VARS.map((v) => (
                  <button key={v} type="button" onClick={() => insertVar(v)} className="text-[11px] font-mono px-2 py-0.5 bg-gray-100 hover:bg-blue-50 hover:text-blue-700 rounded border border-[var(--vv-border)] transition-colors">
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
              <Textarea required rows={14} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="font-mono text-[13px]" data-testid="template-body-textarea"/>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-template-button">{editing ? "Guardar cambios" : "Crear plantilla"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
